import React, { useState, useEffect } from 'react';
import { ChevronDown, Calendar, Users, Clock, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { produksjonAPI } from '../../services/api';
import { beregnSluttDato, erSluttTidNesteDag } from '../../utils/sluttTid';

interface NyProduksjonDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NyProduksjonDialog: React.FC<NyProduksjonDialogProps> = ({ open, onClose, onSuccess }) => {
  // Grunnleggende felt
  const [navn, setNavn] = useState('');
  const [dato, setDato] = useState('');
  const [tid, setTid] = useState('');
  const [sluttTid, setSluttTid] = useState('');
  const [sted, setSted] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [publisert, setPublisert] = useState(false);
  const [kategoriId, setKategoriId] = useState<number | ''>('');

  // Kategorier og mal-data
  const [kategorier, setKategorier] = useState<any[]>([]);
  const [komplettMal, setKomplettMal] = useState<any>(null);
  const [malLoading, setMalLoading] = useState(false);
  const [malHentet, setMalHentet] = useState(false);

  // Loading og error state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Hent kategorier ved mount
  useEffect(() => {
    const fetchKategorier = async () => {
      try {
        const data = await produksjonAPI.getAllKategorier();
        setKategorier(data.kategorier);
      } catch (err) {
        console.error('Feil ved henting av kategorier:', err);
      }
    };

    if (open) {
      fetchKategorier();
    }
  }, [open]);

  // Reset state når dialogen lukkes
  useEffect(() => {
    if (!open) {
      setNavn('');
      setDato('');
      setTid('');
      setSluttTid('');
      setSted('');
      setBeskrivelse('');
      setPublisert(false);
      setKategoriId('');
      setKomplettMal(null);
      setMalHentet(false);
      setError(null);
    }
  }, [open]);

  const handleFyllUtBasertPåKategori = async (katId?: number) => {
    const idToUse = katId || kategoriId;
    if (!idToUse) return;

    setMalLoading(true);
    setError(null);
    try {
      const data = await produksjonAPI.getKomplettMal(idToUse as number);
      setKomplettMal(data);
      setMalHentet(true);

      // Populer felter fra kategori
      if (data.kategori.plassering && !sted) {
        setSted(data.kategori.plassering);
      }
    } catch (err) {
      console.error('Feil ved henting av kategori-mal:', err);
      setError('Kunne ikke hente mal fra kategori');
    } finally {
      setMalLoading(false);
    }
  };

  const handleOpprett = async () => {
    if (!navn || !dato || !tid) {
      setError('Navn, dato og tid er påkrevd');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Kombiner dato og tid til ISO string
      const tidspunkt = new Date(`${dato}T${tid}`).toISOString();

      // Sluttid er valgfri. Er sluttid ≤ starttid, tolkes den som neste dag
      // (f.eks. produksjon fra 23:00 til 01:00). Se utils/sluttTid.ts.
      const sluttTidspunkt: string | undefined = sluttTid
        ? beregnSluttDato(dato, tid, sluttTid).toISOString()
        : undefined;

      await produksjonAPI.createProduksjon({
        navn,
        tid: tidspunkt,
        sluttTid: sluttTidspunkt,
        beskrivelse,
        publisert,
        plassering: sted || undefined,
        kategoriId: kategoriId || undefined,
        applyKategoriMal: malHentet, // Kopier kun hvis mal er hentet
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Feil ved opprettelse av produksjon:', err);
      setError(err.response?.data?.error || 'Kunne ikke opprette produksjon');
    } finally {
      setLoading(false);
    }
  };

  const handleKategoriChange = (value: string) => {
    const newKatId = value ? Number(value) : '';
    setKategoriId(newKatId);
    setKomplettMal(null);
    setMalHentet(false);

    // Automatisk hent mal når mal velges
    if (newKatId) {
      handleFyllUtBasertPåKategori(newKatId);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ny produksjon</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-4">
          {/* Navn */}
          <div className="space-y-2">
            <Label htmlFor="navn-input">Navn på produksjon</Label>
            <Input
              id="navn-input"
              value={navn}
              onChange={(e) => setNavn(e.target.value)}
              required
            />
          </div>

          {/* Dato og Tid */}
          <div className="flex gap-4">
            <div className="w-full space-y-2">
              <Label htmlFor="dato-input">Dato</Label>
              <Input
                id="dato-input"
                type="date"
                value={dato}
                onChange={(e) => setDato(e.target.value)}
                required
              />
            </div>
            <div className="w-full space-y-2">
              <Label htmlFor="tid-input">Starttid</Label>
              <Input
                id="tid-input"
                type="time"
                value={tid}
                onChange={(e) => setTid(e.target.value)}
                required
              />
            </div>
            <div className="w-full space-y-2">
              <Label htmlFor="slutt-tid-input">Sluttid</Label>
              <Input
                id="slutt-tid-input"
                type="time"
                value={sluttTid}
                onChange={(e) => setSluttTid(e.target.value)}
              />
              {erSluttTidNesteDag(tid, sluttTid) && (
                <p className="text-xs text-muted-foreground">Neste dag</p>
              )}
            </div>
          </div>

          {/* Sted */}
          <div className="space-y-2">
            <Label htmlFor="sted-input">Sted</Label>
            <Input id="sted-input" value={sted} onChange={(e) => setSted(e.target.value)} />
          </div>

          {/* Beskrivelse */}
          <div className="space-y-2">
            <Label htmlFor="beskrivelse-input">Beskrivelse</Label>
            <Textarea
              id="beskrivelse-input"
              value={beskrivelse}
              onChange={(e) => setBeskrivelse(e.target.value)}
              rows={3}
            />
          </div>

          {/* Publisert */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="publisert-checkbox"
              checked={publisert}
              onCheckedChange={(checked) => setPublisert(checked === true)}
            />
            <Label htmlFor="publisert-checkbox" className="font-normal">
              Publisert (synlig for alle)
            </Label>
          </div>

          {/* Produksjonsmal */}
          <div className="space-y-2">
            <Label htmlFor="mal-select">Produksjonsmal</Label>
            <Select value={kategoriId ? String(kategoriId) : ''} onValueChange={handleKategoriChange}>
              <SelectTrigger id="mal-select">
                <SelectValue placeholder="Ingen mal" />
              </SelectTrigger>
              <SelectContent>
                {kategorier.map((kat) => (
                  <SelectItem key={kat.id} value={String(kat.id)}>
                    {kat.navn}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status for kategori-mal */}
          {kategoriId && (
            <div className="space-y-3">
              {malLoading && (
                <Alert>
                  <AlertDescription className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Henter mal fra kategori...
                  </AlertDescription>
                </Alert>
              )}

              {malHentet && !malLoading && (
                <Alert className="border-green-200 bg-green-50 text-green-800">
                  <AlertDescription>Mal hentet fra kategori ✓</AlertDescription>
                </Alert>
              )}

              {malHentet && komplettMal && (
                <div className="space-y-2">
                  {/* Plan */}
                  {komplettMal.planMal && komplettMal.planMal.length > 0 && (
                    <Collapsible className="rounded-md border">
                      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-medium">
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-primary" />
                          Plan ({komplettMal.planMal.length} elementer)
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1 px-3 pb-3">
                        {komplettMal.planMal
                          .filter((e: any) => e.type === 'overskrift')
                          .map((overskrift: any) => (
                            <div key={overskrift.id}>
                              <p className="text-sm font-bold">{overskrift.navn}</p>
                              {komplettMal.planMal
                                .filter((h: any) => h.parent_id === overskrift.id)
                                .map((hendelse: any) => (
                                  <p key={hendelse.id} className="pl-4 text-sm text-muted-foreground">
                                    {hendelse.navn} ({hendelse.varighet_minutter} min)
                                  </p>
                                ))}
                            </div>
                          ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Talenter */}
                  {komplettMal.talentMal && komplettMal.talentMal.length > 0 && (
                    <Collapsible className="rounded-md border">
                      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-medium">
                        <span className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-primary" />
                          Talenter ({komplettMal.talentMal.length} typer)
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-2 px-3 pb-3">
                        {komplettMal.talentMal.map((talent: any) => (
                          <div key={talent.id} className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium">{talent.talent_navn}</p>
                              <p className="text-xs text-muted-foreground">
                                {talent.antall} person(er)
                                {talent.beskrivelse ? ` - ${talent.beskrivelse}` : ''}
                              </p>
                            </div>
                            <Badge variant="secondary">{talent.talent_kategori}</Badge>
                          </div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {/* Oppmøtetider */}
                  {komplettMal.oppmoteMal && komplettMal.oppmoteMal.length > 0 && (
                    <Collapsible className="rounded-md border">
                      <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 p-3 text-left text-sm font-medium">
                        <span className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-primary" />
                          Oppmøtetider ({komplettMal.oppmoteMal.length} tider)
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1 px-3 pb-3">
                        {komplettMal.oppmoteMal.map((oppmote: any) => (
                          <div key={oppmote.id}>
                            <p className="text-sm font-medium">{oppmote.navn}</p>
                            <p className="text-xs text-muted-foreground">
                              {oppmote.minutter_før_start} minutter før start
                              {oppmote.beskrivelse ? ` - ${oppmote.beskrivelse}` : ''}
                            </p>
                          </div>
                        ))}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Avbryt
          </Button>
          <Button onClick={handleOpprett} disabled={loading || !navn || !dato || !tid}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Opprett produksjon'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NyProduksjonDialog;
